import { useCallback } from "react";
import { useAppDispatch } from "../app/hooks";
import { registerToastAction } from "../features/toast/toastActions";
import { showToast, type ToastVariant } from "../features/toast/toastSlice";

/** A button on the toast, e.g. { label: "Undo", onAction: () => restore() }. */
export interface ToastActionOptions {
    label: string;
    onAction: () => void;
}

/**
 * Fire-and-forget success/error feedback for any create/update/delete action.
 * Renders via the single <ToastHost/> mounted in AppProviders — callers don't
 * manage visibility, positioning, or the auto-dismiss themselves. An optional
 * action ("Undo") gets a button and a longer time on screen.
 */
export const useToast = () => {
    const dispatch = useAppDispatch();

    const show = useCallback((variant: ToastVariant, message: string, action?: ToastActionOptions) => dispatch(showToast(
        variant, message, action ? { id: registerToastAction(action.onAction), label: action.label } : undefined
    )), [dispatch]);
    const showSuccess = useCallback((message: string, action?: ToastActionOptions) => show("success", message, action), [show]);
    const showError = useCallback((message: string, action?: ToastActionOptions) => show("error", message, action), [show]);

    return { showSuccess, showError };
};
